import React, { useState } from 'react';
import { api, type Bed, type Room } from '../lib/api';
import { OperaWindow, OperaSection, OperaField, type SideButton } from './OperaWindow';

interface Props {
  bed: Bed;
  room: Room;
  onClose: () => void;
  onCreated: () => void;
}

type BlockType = 'MAINTENANCE' | 'DEEP_CLEAN' | 'HOLD' | 'OUT_OF_ORDER';

const BLOCK_TYPE_LABELS: Record<BlockType, string> = {
  MAINTENANCE:  'Maintenance',
  DEEP_CLEAN:   'Deep Clean',
  HOLD:         'Hold',
  OUT_OF_ORDER: 'Out of Order',
};

function todayLocal(): string {
  return new Date().toISOString().slice(0, 10);
}
function plusDays(n: number): string {
  return new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);
}

export function BlockBedModal({ bed, room, onClose, onCreated }: Props) {
  const [blockType, setBlockType] = useState<BlockType>('MAINTENANCE');
  const [reason, setReason] = useState('');
  const [startsAt, setStartsAt] = useState(todayLocal());
  const [endsAt, setEndsAt] = useState(plusDays(1));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    if (!reason.trim()) { setError('Reason is required.'); return; }
    if (new Date(endsAt) <= new Date(startsAt)) {
      setError('End date must be after start date.');
      return;
    }
    setSubmitting(true);
    try {
      await api.createBlock({
        bedId: bed.id,
        blockType,
        reason: reason.trim(),
        startsAt,
        endsAt,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  const bedDesc = room.displayName
    ? `Room ${room.number} (${room.displayName}) — ${bed.label}`
    : `Room ${room.number} — ${bed.label}`;

  const sideButtons: SideButton[] = [
    { label: 'Save',  onClick: handleSubmit, primary: true, disabled: submitting },
    { label: 'Close', onClick: onClose, disabled: submitting },
  ];

  return (
    <OperaWindow
      title="BLOCK BED"
      status={bedDesc.toUpperCase()}
      onClose={onClose}
      size="compact"
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Block Details">
        <OperaField label="Type" required>
          <select value={blockType} onChange={e => setBlockType(e.target.value as BlockType)}>
            {Object.entries(BLOCK_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </OperaField>
        <OperaField label="Reason" required>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Aircon repair, deep clean after long stay"
          />
        </OperaField>
        <OperaField label="From" required>
          <input type="date" value={startsAt} onChange={e => setStartsAt(e.target.value)} />
        </OperaField>
        <OperaField label="Until" required>
          <input type="date" value={endsAt} onChange={e => setEndsAt(e.target.value)} />
        </OperaField>
      </OperaSection>
    </OperaWindow>
  );
}
